import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState } from 'react';
import ChangePasswordModal from './ChangePasswordModal';

const Navbar = () => {
  const { isAdmin, login, logout, adminUsername } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    setError('');
    const success = await login(username, password);
    if (success) {
      setShowLogin(false);
      setUsername('');
      setPassword('');
      navigate('/owner');
    } else {
      setError('Invalid credentials');
    }
  };

  const navLinks = [
    { path: '/', label: 'Features' },
    { path: '/book', label: 'Book' },
    { path: '/calendar', label: 'Calendar' },
    { path: '/confirm', label: 'My Bookings' },
  ];

  const adminLinks = [
    { path: '/manage', label: 'Manage Courts' },
    { path: '/owner', label: 'Dashboard' },
  ];

  const allLinks = isAdmin ? [...navLinks, ...adminLinks] : navLinks;

  return (
    <>
      <nav className="sticky top-0 z-50 bg-gradient-to-r from-green-800 to-green-700 shadow-lg px-3 sm:px-4 py-2 flex items-center justify-between flex-nowrap">
        {/* Logo - always visible */}
        <Link to="/" className="text-white font-bold text-lg sm:text-xl flex items-center gap-1.5 hover:text-yellow-300 transition whitespace-nowrap">
          <span className="text-xl sm:text-2xl">🏸</span>
          <span className="hidden xs:inline">PicklePro</span>
        </Link>

        {/* Desktop menu - hidden on small screens */}
        <div className="hidden md:flex items-center gap-4 lg:gap-6 overflow-hidden">
          {navLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path} 
              className="text-green-100 hover:text-yellow-300 font-medium transition hover:scale-105 text-sm whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
          {isAdmin && adminLinks.map((link) => (
            <Link 
              key={link.path} 
              to={link.path} 
              className="text-yellow-300 hover:text-yellow-200 font-medium transition hover:scale-105 text-sm whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center gap-2">
            {!isAdmin ? (
              <button
                onClick={() => setShowLogin(true)}
                className="px-3 py-1.5 bg-yellow-400 hover:bg-yellow-300 text-green-900 font-bold rounded-lg transition transform hover:scale-105 shadow-md text-sm whitespace-nowrap"
              >
                Admin Login
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-green-100 text-xs whitespace-nowrap">👑 {adminUsername}</span>
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition whitespace-nowrap"
                >
                  🔑
                </button>
                <button
                  onClick={logout}
                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition transform hover:scale-105 shadow-md text-sm whitespace-nowrap"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile hamburger menu - visible on small screens */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white hover:text-yellow-300 focus:outline-none p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed top-12 left-0 right-0 z-40 bg-green-800 shadow-xl py-2 px-4 border-t border-green-700">
          {allLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="block py-2 text-green-100 hover:text-yellow-300 font-medium transition"
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="border-t border-green-700 my-2 pt-2">
            {!isAdmin ? (
              <button
                onClick={() => { setShowLogin(true); setMobileMenuOpen(false); }}
                className="w-full text-left py-2 text-yellow-300 hover:text-yellow-200 font-medium"
              >
                Admin Login
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setShowChangePassword(true); setMobileMenuOpen(false); }}
                  className="w-full text-left py-2 text-blue-300 hover:text-blue-200 font-medium"
                >
                  🔑 Change Password
                </button>
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="w-full text-left py-2 text-red-300 hover:text-red-200 font-medium"
                >
                  Logout ({adminUsername})
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Login Modal - without default credentials note */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Admin Login</h3>
            {error && (
              <div className="mb-3 p-2 bg-red-50 text-red-600 text-sm rounded border border-red-200">
                {error}
              </div>
            )}
            <input
              type="text"
              placeholder="Username"
              className="w-full border rounded px-3 py-2 mb-3"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              className="w-full border rounded px-3 py-2 mb-4"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowLogin(false); setUsername(''); setPassword(''); setError(''); }}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleLogin}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Login
              </button>
            </div>
            {/* Default credentials note REMOVED */}
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />
    </>
  );
};

export default Navbar;